import React from 'react';
import { useTerminology } from '../../hooks/useTerminology';

/**
 * One operator table, two presentations — because a dense grid and a 390px
 * screen are genuinely different problems.
 *
 * Above `md` this renders a real <table>, which is what a person scanning
 * forty rows of rent or applications actually needs. Below it, each row
 * becomes a card with its column labels attached, so nothing has to be
 * horizontally scrolled to be read. The row's primary column leads the card
 * and its status sits beside it; everything else is a labelled pair.
 *
 * Callers describe the data once. They cannot produce a desktop-only table
 * by accident.
 */

export interface Column<Row> {
  /** Column heading. Also becomes the field label on the mobile card. */
  header: string;
  /** Cell content. */
  cell: (row: Row) => React.ReactNode;
  /** Marks the column that identifies the row — it leads the mobile card. */
  primary?: boolean;
  /** Marks the status/outcome column — it sits beside the primary on mobile. */
  status?: boolean;
  /** Right-align in the table (used for amounts). */
  align?: 'left' | 'right';
  /** Hide from the mobile card when the primary line already carries it. */
  hideOnCard?: boolean;
  className?: string;
}
interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Rendered in place of the table when there is nothing to show. */
  empty?: React.ReactNode;
  onRowClick?: (row: Row) => void;
}
export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  empty,
  onRowClick
}: DataTableProps<Row>) {
  /* Column headings and the table's caption resolve terminology, so renaming a
     concept reaches operator tables as well as navigation. Keys and lookups
     stay on the raw header text, so a rename cannot reshuffle a table. */
  const {
    phrase
  } = useTerminology();
  if (rows.length === 0 && empty) return <>{empty}</>;
  const primary = columns.find(column => column.primary) ?? columns[0];
  const status = columns.find(column => column.status);
  const rest = columns.filter(column => column !== primary && column !== status && !column.hideOnCard);
  return <>
      {/* Desktop: a real table. */}
      <div className="hidden overflow-x-auto md:block" data-id="element-5863" data-mp="2edd33v4uqto">
        <table className="w-full text-left" data-id="element-5864" data-mp="7cyeh6m4fgjp">
          <caption className="sr-only" data-id="element-5865" data-mp="7bn9x4kuq4rj">{phrase(caption)}</caption>
          <thead data-id="element-5866" data-mp="3pibbwlklubsz">
            <tr className="border-b border-line" data-id="element-5867" data-mp="3phrxheu5y5jp">
              {columns.map(column => <th key={column.header} scope="col" className={`whitespace-nowrap px-3 py-3 text-caption font-semibold uppercase tracking-wide text-ink-muted ${column.align === 'right' ? 'text-right' : ''}`} data-id="element-5868" data-mp="3uk0s0fmopfnx">
                  {phrase(column.header)}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-5869" data-mp="1iqixxh5e1ikr">
            {rows.map(row => <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} className={onRowClick ? 'cursor-pointer hover:bg-canvas' : undefined} data-id="element-5870" data-mp="338uvr9vgeij2">
                {columns.map(column => <td key={column.header} className={`px-3 py-3 align-top text-body-sm text-ink ${column.align === 'right' ? 'text-right' : ''} ${column.className ?? ''}`} data-id="element-5871" data-mp="3898uu2f2ui4y">
                    {column.cell(row)}
                  </td>)}
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Mobile: the same rows as readable cards, never a squeezed grid. */}
      <ul className="divide-y divide-line md:hidden" data-id="element-5872" data-mp="21m3s2qv0nchx">
        {rows.map(row => <li key={rowKey(row)} className="p-4" data-id="element-5873" data-mp="1wnsjejggj81a">
            <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-5874" data-mp="1y3o8jwjij2oe">
              <p className="min-w-0 text-body font-medium text-ink" data-id="element-5875" data-mp="1y5qwmb13ulqh">{primary.cell(row)}</p>
              {status && <div className="shrink-0" data-id="element-5876" data-mp="234kc28zycmta">{status.cell(row)}</div>}
            </div>
            {rest.length > 0 && <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2" data-id="element-5877" data-mp="2d0x6xa78h6pm">
                {rest.map(column => <div key={column.header} className="min-w-0" data-id="element-5878" data-mp="1fdonrlupg9qs">
                    <dt className="text-caption text-ink-subtle" data-id="element-5879" data-mp="1fb28f01kopy0">{phrase(column.header)}</dt>
                    <dd className="text-body-sm text-ink" data-id="element-5880" data-mp="1ka5coi9qas4x">{column.cell(row)}</dd>
                  </div>)}
              </dl>}
            {onRowClick && <button type="button" onClick={() => onRowClick(row)} className="mt-3 min-h-[44px] text-body-sm font-medium text-brand-700 hover:underline" data-id="element-5881" data-mp="1u3m4jgqb1x5y">
                Open
              </button>}
          </li>)}
      </ul>
    </>;
}